import { useMemo, useState } from "react";
import { FinishedJobTail } from "../components/FinishedJobRow";
import { NotificationRow, RunningJobRow, WarningRow } from "../components/OpsRows.js";
import { QueuedJobRow, QueuedJobTail } from "../components/QueuedJobRow.js";
import { PanelTail, TAIL_MAX } from "../components/PanelTail.js";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type DatabaseSize,
  type ExtractBacklogScope,
  type ExtractBacklogState,
  type OpsActivity,
} from "../api/client.js";
import { chicagoTime, formatBytes, formatDuration, scaleDecimal } from "../lib/format.js";
import { useDocumentVisible } from "../lib/visibility.js";

/**
 * Pipeline health. The corpus growing quietly wrong is hard to notice from anywhere
 * else, so the queue, the warnings and the dependency checks are all one screen.
 *
 * **The screen is polled, not streamed.** The worker writes to Postgres and tells nobody,
 * so an SSE endpoint would have to poll Postgres itself and push the result: the same
 * freshness, plus a long-lived connection through the reverse proxy and a reconnection
 * state machine to get wrong. A poll also costs nothing while the tab is hidden.
 */


/**
 * A section that can be folded away, so a long tail does not push everything below it
 * off a phone screen. Collapsing is component state rather than a stored setting: a
 * preference that outlives the reason it was set becomes a panel the owner does not know
 * they are hiding.
 *
 * The count stays visible while collapsed: a folded panel that says nothing looks the
 * same whether it holds two rows or two hundred.
 */
function Panel({
  title,
  count,
  countLabel,
  children,
}: {
  title: string;
  count?: number | null;
  countLabel?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <>
      <div className="sechd">
        <button
          type="button"
          className="disclose"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span aria-hidden="true">{open ? "\u25be" : "\u25b8"}</span> {title}
        </button>
        {!open && count != null && (
          <span className="sechd-count">
            {count}
            {countLabel ? ` ${countLabel}` : ""}
          </span>
        )}
      </div>
      {open && children}
    </>
  );
}

/** A row that opens to show, untruncated, what the list had to cut. */
function ExpandableRow({
  id,
  openId,
  setOpenId,
  summary,
  detail,
}: {
  id: string;
  openId: string | null;
  setOpenId: (id: string | null) => void;
  summary: React.ReactNode;
  detail: React.ReactNode;
}) {
  const open = openId === id;
  return (
    <>
      <button
        type="button"
        className="filing rowbtn"
        aria-expanded={open}
        onClick={() => setOpenId(open ? null : id)}
      >
        {summary}
      </button>
      {open && <div className="rowdetail">{detail}</div>}
    </>
  );
}

/**
 * How often the live panels ask, while somebody is looking.
 *
 * Two seconds is chosen against what it is watching: `fetch_filing` and `extract_text`
 * take seconds, so a slower poll would show a queue that jumps rather than moves, and a
 * faster one would only re-render the same numbers. See `useDocumentVisible` for the
 * other half — this interval applies only while the tab is on screen.
 */
const ACTIVITY_POLL_MS = 2_000;

/**
 * The operations page at `/ops`: what the pipeline is doing now, the semantic index,
 * backfill and re-read controls, the review lists, cost, dependencies and storage.
 */
export function OpsPage() {
  const queryClient = useQueryClient();
  /**
   * Everything that does not move between one poll and the next is on a slower one.
   * `/api/jobs` counts every row in the queue including the ever-growing `done` pile, and
   * a failure parked for an hour is not more urgent for being re-read every few seconds.
   */
  const jobs = useQuery({ queryKey: ["jobs"], queryFn: api.jobs, refetchInterval: 30_000 });
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const warnings = useQuery({ queryKey: ["warnings"], queryFn: () => api.warnings() });
  const health = useQuery({ queryKey: ["health"], queryFn: api.health, refetchInterval: 30_000 });
  const version = useQuery({ queryKey: ["version"], queryFn: api.version });

  const visible = useDocumentVisible();
  /**
   * One control for the fast poll: a tail that rewrites itself every two seconds is hard
   * to read a row out of, and this page is also opened over cellular. It is component
   * state and not a setting, so the poll is never left silently switched off.
   */
  const [live, setLive] = useState(true);
  /**
   * Which row is opened, per list, held by the row's own id rather than its index: the
   * snapshot behind these lists refreshes on the fast poll, and an index would slide an
   * open panel onto a different row mid-read. One at a time.
   */
  const [openNotification, setOpenNotification] = useState<string | null>(null);
  const [openWarning, setOpenWarning] = useState<string | null>(null);
  const [openRunning, setOpenRunning] = useState<string | null>(null);
  const activity = useQuery({
    queryKey: ["opsActivity"],
    queryFn: api.opsActivity,
    refetchInterval: live && visible ? ACTIVITY_POLL_MS : false,
  });

  const sync = useMutation({
    mutationFn: () => api.sync(),
    onSuccess: () => invalidateQueue(),
  });
  // Through the typed client, not a bare fetch: `fetch` resolves on a 404 or a 401, so a
  // retry the server refused would read as success.
  const retry = useMutation({
    mutationFn: (jobId: string) => api.retryJob(jobId),
    onSuccess: () => invalidateQueue(),
  });

  /**
   * The queue's two views, refreshed together. A press that moved the queue but left
   * the live panel showing the state before it reads as a button that did nothing.
   */
  async function invalidateQueue(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ["jobs"] });
    await queryClient.invalidateQueries({ queryKey: ["opsActivity"] });
  }

  const notify = useQuery({
    queryKey: ["notifications"],
    queryFn: api.notifications,
    refetchInterval: 30_000,
  });
  /**
   * The embedding backfill. A document with no chunks is invisible to the semantic half
   * of search, and unlike a missing extraction that failure shows nowhere: paraphrase
   * queries simply come back thin.
   */
  const backfill = useQuery({
    queryKey: ["embedBackfill"],
    queryFn: api.embedBackfillEstimate,
    /**
     * Refreshed only while a pass is in flight, and then only once a minute: this
     * estimate is an expensive query, so it is kept off the fast poll. The live chunk
     * rate comes from the cheap endpoint instead.
     */
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && (data.queued > 0 || data.running > 0) ? 60_000 : false;
    },
  });
  /**
   * Stop or resume a kind of work. Paused jobs stay queued and run when the pause lifts,
   * so pausing the daily scan accumulates one job per day rather than losing them.
   */
  const pause = useMutation({
    mutationFn: ({ kind, paused }: { kind: string; paused: boolean }) =>
      api.pauseKind(kind, paused),
    onSuccess: async () => {
      await invalidateQueue();
      await queryClient.invalidateQueries({ queryKey: ["embedBackfill"] });
      await queryClient.invalidateQueries({ queryKey: ["form10Backfill"] });
      await queryClient.invalidateQueries({ queryKey: ["retriage"] });
    },
  });
  /**
   * Read from the live snapshot rather than from `/api/jobs`, so the pause controls and
   * the paused markers in the queue agree within one poll instead of within thirty
   * seconds. Both endpoints report the same set; this is the one that is fresh.
   */
  const isPaused = (kind: string) =>
    activity.data?.pausedKinds.includes(kind) ?? jobs.data?.pausedKinds.includes(kind) ?? false;
  /** Absent when the size query failed; the reachability check is unaffected by that. */
  const size = health.data?.database.size ?? null;

  /**
   * The historical pass over `10-12B`, the one form that is a separation by construction.
   *
   * How far back to walk is chosen before pressing rather than assumed: a short range is
   * a trial that shows what the pass surfaces before the host is committed for days.
   */
  const [years, setYears] = useState(2);
  const range = useMemo(() => {
    const today = new Date();
    return {
      from: new Date(Date.UTC(today.getUTCFullYear() - years, today.getUTCMonth(), 1))
        .toISOString()
        .slice(0, 10),
      to: today.toISOString().slice(0, 10),
    };
  }, [years]);

  const form10 = useQuery({
    queryKey: ["form10Backfill", range.from, range.to],
    queryFn: () => api.form10Backfill(range),
  });
  const startForm10 = useMutation({
    mutationFn: () => api.startForm10Backfill(range),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["form10Backfill"] });
      await queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });

  const startBackfill = useMutation({
    mutationFn: () => api.embedBackfill(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["embedBackfill"] });
      await queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });

  /**
   * A change to the Screen's rules does not reach backwards on its own, so nothing
   * already on the radar is re-judged behind the owner's back. This is that pass, run
   * deliberately.
   */
  const retriage = useQuery({ queryKey: ["retriage"], queryFn: api.retriage });
  const tables = useQuery({ queryKey: ["tables-backfill"], queryFn: api.tablesBackfill });
  /**
   * The dates a Form 10 never carried. An information statement is filed "Subject to
   * Completion" with the record and distribution dates blank in every version; the
   * parent announces them later in an 8-K, and the historical walk fetches `10-12B` and
   * `10-12B/A` only. So a situation completed years ago can hold a ratio, a ticker and a
   * tax treatment with no dates and nothing anywhere that could have supplied them.
   */
  const terms = useQuery({ queryKey: ["terms-backfill"], queryFn: api.termsBackfill });
  /**
   * Filings by a filer already on the radar that were fetched and extracted but attached
   * to no situation, so their terms were never read.
   */
  const attachments = useQuery({
    queryKey: ["attachment-backfill"],
    queryFn: api.attachmentBackfill,
  });
  /**
   * Pairs the situation already holds, read by term patterns that have since changed.
   * `TERMS_VERSION` is what lets a pair already read be read again.
   */
  const termsRerun = useQuery({ queryKey: ["terms-rerun"], queryFn: api.termsRerun });
  /**
   * Companies carrying more than one live situation. Folding them is manual, because a
   * wrong merge is far more expensive to notice and undo than two rows are to read.
   */
  const duplicates = useQuery({
    queryKey: ["duplicate-situations"],
    queryFn: api.duplicateSituations,
  });
  const unplaced = useQuery({ queryKey: ["unplaced-filings"], queryFn: api.unplacedFilings });

  /**
   * What the pass found, and the only thing it does with it. Nothing is retired,
   * archived or dismissed automatically: a rule change is not new evidence.
   */
  const unsupported = useQuery({
    queryKey: ["unsupportedSituations"],
    queryFn: api.unsupportedSituations,
  });
  /**
   * Situations no automatic read has reached. Reading the backlog is worth doing for the
   * verdict: a read can answer "this is not a separation" and dismiss the situation
   * itself, which is the cheapest path through the situations the report below lists.
   */
  const backlog = useQuery({ queryKey: ["extractBacklog"], queryFn: api.extractBacklog });
  const startBacklog = useMutation({
    mutationFn: (scope: ExtractBacklogScope) => api.startExtractBacklog(scope),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["extractBacklog"] });
      await queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });

  const startAttachments = useMutation({
    mutationFn: () => api.startAttachmentBackfill(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["attachment-backfill"] });
      await queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
  const startTermsRerun = useMutation({
    mutationFn: () => api.startTermsRerun(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["terms-rerun"] });
      await queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
  const startTerms = useMutation({
    mutationFn: () => api.startTermsBackfill(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["terms-backfill"] });
      await queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
  const startTables = useMutation({
    mutationFn: () => api.startTablesBackfill(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tables-backfill"] });
    },
  });
  const startRetriage = useMutation({
    mutationFn: () => api.startRetriage(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["retriage"] });
      await queryClient.invalidateQueries({ queryKey: ["unsupportedSituations"] });
      await queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });

  return (
    <div className="read">
      <div className="rtop">
        <div className="rtopin">
          <div className="crumb">Radar › Operations</div>
          <div className="acts">
            {/* Not "today": EDGAR has not published today's index while today is
                happening, and asking for it returns 403. */}
            <button className="iconbtn" onClick={() => sync.mutate()} disabled={sync.isPending}>
              {sync.isPending ? "Enqueuing…" : "Scan last trading day"}
            </button>
            {/*
              Pausing the SCAN pauses the kind, not the scheduler. Each day is enqueued
              under its own dedupe key, so a pause accumulates one job per day and
              resuming works through every missed day rather than only the latest.
            */}
            <button
              className="iconbtn"
              aria-pressed={isPaused("scan_daily_index")}
              disabled={pause.isPending}
              onClick={() =>
                pause.mutate({ kind: "scan_daily_index", paused: !isPaused("scan_daily_index") })
              }
            >
              {isPaused("scan_daily_index") ? "Resume scanning" : "Pause scanning"}
            </button>
          </div>
        </div>
      </div>

      <article className="article">
        <div className="eyebrow">
          <span>Operations</span>
        </div>
        <h1>Pipeline</h1>
        <p className="deck">
          The worker is the only process that talks to EDGAR, and it is the one whose
          failure looks like nothing happening at all.
        </p>

        {me.data?.unguarded && (
          <div className="notice">
            <b>Running unguarded</b>
            No OAuth credentials are configured, so this API is not requiring a session.
            Set GITHUB_OAUTH_CLIENT_ID, GITHUB_OAUTH_CLIENT_SECRET and
            ALLOWED_GITHUB_USER_ID to enforce one. Production refuses to run this way.
          </div>
        )}

        {(sync.isError || retry.isError) && (
          <div className="notice">
            <b>That did not go through</b>
            {((sync.error ?? retry.error) as Error | undefined)?.message ??
              "The request failed."}
          </div>
        )}

        {sync.data && (
          <div className="notice">
            <b>{sync.data.alreadyQueued ? "Already queued" : "Scan enqueued"}</b>
            {sync.data.alreadyQueued
              ? `A scan of ${sync.data.date} is already in the queue.`
              : `Walking ${sync.data.date}'s daily index — the last completed trading day, since today's is not published yet. The API never calls EDGAR itself; the worker picks this up.`}
          </div>
        )}

        {/*
          What the machine is doing this second. A running count cannot distinguish a
          worker steadily walking many documents from one wedged on the first, or from one
          that died holding the lock; the kind, the subject, the elapsed time and the lease
          age separate all three.
        */}
        <div className="sechd">Now</div>
        <p className="ops-actions">
          <button className="iconbtn" aria-pressed={live} onClick={() => setLive(!live)}>
            {live ? "Live" : "Paused"}
          </button>
          {/*
            The snapshot's own timestamp, absolute rather than "3s ago". A relative age
            computed at render freezes the moment the polling stops — while paused, while
            the tab is hidden, or while every request is failing — and then misstates how
            fresh the panel is.
          */}
          <span className="hint">{cadenceLabel(activity.data ?? null, live, visible)}</span>
        </p>

        {activity.isError && (
          <div className="notice">
            <b>The live poll is failing</b>
            {(activity.error as Error | undefined)?.message ?? "The request failed."} Anything
            below is{" "}
            {activity.data
              ? `from ${new Date(activity.data.observedAt).toLocaleTimeString()} and is not moving.`
              : "not available at all."}
          </div>
        )}

        {activity.data && activity.data.running.length > 0 && (
          <div className="filings live">
            {activity.data.running.map((job) => (
              <RunningJobRow
                key={job.id}
                job={job}
                open={openRunning === job.id}
                onToggle={() => setOpenRunning(openRunning === job.id ? null : job.id)}
                leaseNote={leaseLabel(job)}
                elapsed={elapsedLabel(job)}
              />
            ))}
          </div>
        )}

        {/*
          Said, not left blank. An empty panel is what a stopped pipeline looks like and
          also what an idle one looks like, and the difference is the whole reason this
          screen exists — so the sentence names which of the three it is: nothing to do,
          nothing runnable, or work that is runnable and unclaimed.
        */}
        {activity.data && activity.data.running.length === 0 && (
          <p className="deck">{idleSentence(activity.data)}</p>
        )}

        <Panel
          title="Queue"
          count={totalOf(activity.data, "pending") || null}
          countLabel="queued"
        >
        <div className="terms">
          <div>
            <div className="k">Queued</div>
            <div className="v">{totalOf(activity.data, "pending").toLocaleString()}</div>
            <div className="src">
              {activity.data ? `across ${activity.data.backlog.length} kinds` : "—"}
            </div>
          </div>
          <div>
            <div className="k">Running</div>
            <div className="v">{activity.data?.running.length ?? "—"}</div>
            <div className="src">claimed right now</div>
          </div>
          <div>
            <div className="k">Failed</div>
            <div className={`v${totalOf(activity.data, "failed") > 0 ? " hi" : ""}`}>
              {totalOf(activity.data, "failed").toLocaleString()}
            </div>
            <div className="src">parked, awaiting a decision</div>
          </div>
          {/*
            A running count is not self-explaining. One worker with a stale claim is a
            deploy that interrupted a job, and it clears itself; two workers is a stray
            process to go and kill. Same number, opposite responses, so both are shown.
          */}
          {activity.data && (
            <div>
              <div className="k">workers</div>
              <div className={`v${activity.data.workers > 1 ? " hi" : ""}`}>
                {activity.data.workers}
              </div>
              <div className="src">
                {activity.data.workers > 1 ? "one is a mistake" : "expected: 1"}
              </div>
            </div>
          )}
          {/*
            Counted apart from workers, because they mean opposite things. A second
            worker is a stray process to go and kill; a second executor is the agent path
            working as designed. Shown only when one is holding a claim, since a zero is
            noise rather than news.
          */}
          {activity.data && activity.data.executors > 0 && (
            <div>
              <div className="k">executors</div>
              <div className="v">{activity.data.executors}</div>
              <div className="src">
                {activity.data.executors === 1 ? "a session, reading" : "sessions, reading"}
              </div>
            </div>
          )}
          {activity.data && activity.data.staleClaims > 0 && (
            <div>
              <div className="k">stale claims</div>
              <div className="v">{activity.data.staleClaims}</div>
              <div className="src">a stopped worker — reclaimed shortly</div>
            </div>
          )}
        </div>

        {/*
          Two lists, in the order a reader drills: the totals above, then the same queue
          by kind, then the jobs themselves.

          **Both are labelled.** They share the `.filings live` treatment, so stacked with
          nothing between them they read as one list that changes shape halfway down. The
          labels say which question each answers: where the backlog is sitting, and what
          runs next.
        */}
        <p className="listhd">By kind — where the backlog is sitting</p>
        {/*
          Per kind, because a global depth says the machine is busy and this says which
          stage it is stuck at. No per-row list can answer that, so the record tail below
          does not replace this.
        */}
        {activity.data && activity.data.backlog.length > 0 && (
          <div className="filings live">
            {activity.data.backlog.map((kind) => (
              <div className="filing" key={kind.kind}>
                <span className="ft">{kind.kind}</span>
                <span className="fd">{kind.pending.toLocaleString()} queued</span>
                <span className="fx">
                  {kind.running > 0 ? `${kind.running} running · ` : ""}
                  {kind.failed > 0 ? `${kind.failed} failed · ` : ""}
                  {waitLabel(kind.oldestPendingSeconds)}
                </span>
                {kind.paused && <span className="ff">paused</span>}
              </div>
            ))}
          </div>
        )}
        {activity.data && activity.data.backlog.length === 0 && (
          <p className="deck">Nothing is pending, running or failed. The queue is empty.</p>
        )}

        {/*
          The jobs themselves, in the order a worker would claim them, as a tail with a
          link to the rest. The total above keeps the tail's cap from being a silent one.
        */}
        <p className="listhd">Next to run — in claim order</p>
        {activity.data && activity.data.queued.length > 0 && (
          <>
            <QueuedJobTail jobs={activity.data.queued} />
            <p className="fin-caveat">
              {totalOf(activity.data, "pending").toLocaleString()} queued in all, across{" "}
              {activity.data.backlog.filter((k) => k.pending > 0).length} kind
              {activity.data.backlog.filter((k) => k.pending > 0).length === 1 ? "" : "s"} —
              the breakdown is above. Claimed in order of priority, then by when each became
              runnable, which is the order these are listed in.
            </p>
          </>
        )}
        {/*
          Said, not left blank. Nothing queued at all and a queue holding only work that
          is running are different states, and an empty tail is what both look like.
        */}
        {activity.data && activity.data.queued.length === 0 && (
          <p className="deck">{queuedQuietSentence(activity.data)}</p>
        )}

        {/*
          What is paused, in one sentence and one place, rather than inferred from what
          each button offers to do.
        */}
        {activity.data && (
          <p className="fin-caveat">
            {activity.data.pausedKinds.length === 0
              ? "Nothing is paused."
              : `Paused: ${activity.data.pausedKinds.join(", ")}. Paused work is skipped at claim time, never dropped — it runs when the pause lifts.`}
          </p>
        )}
        </Panel>

        {/*
          A pipeline that has stopped has no recent failures either. A tail of completions
          is what says the machine is alive, so successes and failures share one list in
          the order they happened.
        */}
        <Panel
          title="Just finished"
          count={Math.min(activity.data?.recent.length ?? 0, TAIL_MAX) || null}
        >
        {activity.data && activity.data.recent.length > 0 && (
          <>
            {/*
              A short tail with a link to the rest: every row the response carried,
              rewriting itself on the fast poll, would be a list nobody can read a row out
              of.
            */}
            <FinishedJobTail jobs={activity.data.recent} />
            <p className="fin-caveat">
              {activity.data.throughput.completed} finished and{" "}
              {activity.data.throughput.failed} failed in the last{" "}
              {activity.data.throughput.windowMinutes} minutes. Read back from
              jobs.finished_at, newest first — a successful job keeps its row, so this is
              a record rather than a reconstruction.
            </p>
          </>
        )}
        {/*
          An empty tail means one of two things: nothing has finished recently, or no job
          has recorded a finish time at all. A blank space says neither, so the flag from
          the server picks the sentence.
        */}
        {activity.data && activity.data.recent.length === 0 && (
          <p className="deck">
            {activity.data.completionsRecorded
              ? "Nothing has finished recently."
              : "No job has recorded a finish time yet — timing starts with the first job to finish after this deploy."}
          </p>
        )}
        </Panel>

        {/*
          Kept beside the tail rather than folded into it: the tail answers "is it
          moving", this answers "what is parked and needs a decision from me", and only
          the second one carries a control. It is on the slow poll: a failure an hour old
          is not more urgent for being re-read every two seconds.
        */}
        {jobs.data && jobs.data.recentFailures.length > 0 && (
          <Panel title="Parked" count={jobs.data.failuresTotal || null} countLabel="failed">
            {/*
              The same record and the same renderer as the queue tail, because a parked
              job is a queue row that stopped.

              **The Retry control is in the opened row**, and not only because a button
              cannot nest inside the row's own button: retrying is a decision, and the
              error it should be made on is what the summary has to truncate.
            */}
            <PanelTail
              items={jobs.data.recentFailures}
              idOf={(f) => f.id}
              renderRow={(f, open, toggle) => (
                <QueuedJobRow
                  job={f}
                  open={open}
                  onToggle={toggle}
                  action={
                    <button
                      className="iconbtn"
                      disabled={retry.isPending}
                      onClick={() => retry.mutate(f.id)}
                    >
                      {retry.isPending && retry.variables === f.id ? "Requeuing…" : "Retry"}
                    </button>
                  }
                />
              )}
              href="/ops/queue?state=failed"
              linkLabel="All parked jobs"
            />
            {/*
              How many there really are: a capped list otherwise reads exactly like the
              whole of it.
            */}
            {jobs.data.failuresTotal > jobs.data.recentFailures.length && (
              <p className="fin-caveat">
                {jobs.data.failuresTotal.toLocaleString()} parked in all — the newest{" "}
                {jobs.data.recentFailures.length} are here. Nothing is ever dropped; a
                parked job keeps its row until it is retried.
              </p>
            )}
          </Panel>
        )}

        <div className="sechd">Semantic index</div>
        {backfill.data && (
          <>
            <div className="terms">
              <div>
                <div className="k">Not embedded</div>
                <div className={`v${backfill.data.pending > 0 ? " hi" : ""}`}>
                  {backfill.data.pending}
                </div>
                <div className="src">documents worth indexing</div>
              </div>
              <div>
                <div className="k">Embedded</div>
                <div className="v">{backfill.data.embedded}</div>
                <div className="src">searchable by meaning</div>
              </div>
              <div>
                <div className="k">Est. chunks</div>
                <div className="v">{backfill.data.estimatedChunks.toLocaleString()}</div>
                <div className="src">at 1,500 chars each</div>
              </div>
              {/*
                Hours, not dollars. Embeddings run on the host's own CPU and touch no paid
                API, but a pass can occupy the machine for hours, so the time is quoted
                before the press.
              */}
              <div>
                <div className="k">Est. time</div>
                <div className="v">
                  {backfill.data.estimatedHours >= 1
                    ? `${backfill.data.estimatedHours.toFixed(1)} h`
                    : `${Math.round(backfill.data.estimatedHours * 60)} min`}
                </div>
                {/*
                  Which rate this used, not just what it was. Once this host has run a
                  substantial pass of its own, that is a better predictor than the bench
                  figure, because it includes whatever else the machine was doing.
                */}
                <div className="src">
                  {backfill.data.chunksPerSecond}/s{" "}
                  {backfill.data.chunksPerSecondSource === "observed" ? "observed here" : "measured"}{" "}
                  · {backfill.data.model}
                </div>
              </div>
            </div>

            {/*
              What is happening this minute, against what the estimate above assumed.
              The estimate cannot say whether anything is being written *now*. `last chunk
              N ago` is what makes a wedged Ollama legible: a running `embed_document` with
              no chunk written for minutes is a sentence to act on.
            */}
            {activity.data && (
              <p className="fin-caveat">{embeddingLine(activity.data, backfill.data)}</p>
            )}

            {/*
              What the last pass did. Nothing records which jobs belonged to one pass, so
              it is reconstructed from `chunks.embedded_at`.
            */}
            {backfill.data.lastPass && (
              <p className="fin-caveat">
                Last pass: {backfill.data.lastPass.chunks.toLocaleString()} chunks across{" "}
                {backfill.data.lastPass.documents.toLocaleString()} document
                {backfill.data.lastPass.documents === 1 ? "" : "s"} in{" "}
                {formatDuration(backfill.data.lastPass.seconds)}, finishing{" "}
                {new Date(backfill.data.lastPass.finishedAt).toLocaleString()} at{" "}
                {backfill.data.lastPass.chunksPerSecond}/s. Measured between the first and
                last chunk written, so it excludes any stretch the queue spent paused.
              </p>
            )}

            {/*
              The jobs run without Ollama and write one warning per document instead of
              embedding anything, so a missing configuration is said before the press
              rather than found afterwards.
            */}
            {!backfill.data.ollamaConfigured && (
              <div className="notice">
                <b>Ollama is not configured</b>
                These jobs would run and record a warning per document instead of
                embedding anything. Set OLLAMA_BASE_URL before starting a pass.
              </div>
            )}

            {startBackfill.data && (
              <div className="notice">
                <b>
                  {startBackfill.data.queued > 0
                    ? `${startBackfill.data.queued} queued`
                    : "Nothing new to queue"}
                </b>
                {startBackfill.data.alreadyQueued > 0
                  ? `${startBackfill.data.alreadyQueued} were already in the queue. `
                  : ""}
                They run at backfill priority, below everything that moves a filing
                toward a situation, so the morning scan is never delayed by this.
              </div>
            )}

            {/*
              What the predicate selected. A bare count has no way to check it; this makes
              the tiering decision readable, and a type that looks wrong here shows the rule
              has drifted from what the corpus holds.
            */}
            {backfill.data.byType.length > 0 && (
              <p className="fin-caveat">
                {backfill.data.byType
                  .map((t) => `${t.documents} × ${t.documentType}`)
                  .join(" · ")}
                {" — everything else stays searchable by phrase, just not by meaning."}
              </p>
            )}

            {/*
              The controls reflect what is in flight: nothing to do, work queued, or
              paused. The start button is absent while a pass is queued, where a second
              press would do nothing.
            */}
            <p className="ops-actions">
              {backfill.data.pending > 0 && backfill.data.queued === 0 && (
                <button
                  className="iconbtn"
                  onClick={() => startBackfill.mutate()}
                  disabled={startBackfill.isPending || backfill.data.paused}
                >
                  {startBackfill.isPending
                    ? "Queuing…"
                    : `Embed ${backfill.data.pending} document${backfill.data.pending === 1 ? "" : "s"}`}
                </button>
              )}

              {(backfill.data.queued > 0 || backfill.data.running > 0) && (
                <span className="hint">
                  {backfill.data.running > 0 ? `${backfill.data.running} running · ` : ""}
                  {backfill.data.queued} queued
                  {backfill.data.paused ? " · paused" : ""}
                </span>
              )}

              {(backfill.data.queued > 0 ||
                backfill.data.running > 0 ||
                backfill.data.paused) && (
                <button
                  className="iconbtn"
                  aria-pressed={backfill.data.paused}
                  disabled={pause.isPending}
                  onClick={() =>
                    pause.mutate({ kind: "embed_document", paused: !backfill.data!.paused })
                  }
                >
                  {backfill.data.paused ? "Resume embedding" : "Pause embedding"}
                </button>
              )}

              {backfill.data.pending === 0 &&
                backfill.data.queued === 0 &&
                backfill.data.running === 0 && (
                  <span className="hint">Everything worth indexing is embedded.</span>
                )}
            </p>
          </>
        )}

        {/*
          History, walked by the one form that cannot be anything else, rather than by
          every candidate form, nearly all of which never became a separation.
        */}
        <div className="sechd">Historical Form 10s</div>
        {/*
          The range is chosen before the estimate is read, because the estimate is about
          the range. The shortest range is the cheap way to see what the pass surfaces.
        */}
        <p className="ops-actions">
          {([2, 5, 15] as const).map((y) => (
            <button
              key={y}
              className="iconbtn"
              aria-pressed={years === y}
              onClick={() => setYears(y)}
            >
              {y} years
            </button>
          ))}
        </p>
        {form10.data && (
          <>
            <div className="terms">
              <div>
                <div className="k">Range</div>
                <div className="v">{form10.data.quarters} qtr</div>
                <div className="src">
                  {form10.data.from.slice(0, 7)} → {form10.data.to.slice(0, 7)}
                </div>
              </div>
              <div>
                <div className="k">Est. filings</div>
                <div className="v">{form10.data.projectedFilings.toLocaleString()}</div>
                {/* Named as a projection, not a measurement — nothing knows the real
                    count until the quarterly indexes are walked. */}
                <div className="src">projected at ~42/yr</div>
              </div>
              <div>
                <div className="k">EDGAR requests</div>
                <div className="v">{form10.data.projectedEdgarRequests.toLocaleString()}</div>
                <div className="src">free, but rate-limited</div>
              </div>
              <div>
                <div className="k">Est. embedding</div>
                <div className={`v${form10.data.embeddingPaused ? " unknown" : ""}`}>
                  {form10.data.embeddingPaused
                    ? "paused"
                    : `${form10.data.projectedEmbedHours.toFixed(0)} h`}
                </div>
                {/*
                  The rate, and whether it is this deployment's own. The same model and
                  batch run at very different rates on different hosts, so a quote built
                  from a constant can be wrong by an order of magnitude.
                */}
                <div className="src">
                  {form10.data.projectedChunks.toLocaleString()} chunks at{" "}
                  {form10.data.embedChunksPerSec}/s{" "}
                  {form10.data.embedRateMeasured ? "measured here" : "(bench, not measured here)"}
                </div>
              </div>
            </div>

            {/*
              Embedding is most of what this costs, and it is a separate control. Said
              here rather than left to be discovered when the machine is busy for days.
            */}
            {!form10.data.embeddingPaused && form10.data.projectedEmbedHours > 24 && (
              <div className="notice">
                <b>
                  This queues roughly {form10.data.projectedEmbedHours.toFixed(0)} hours of
                  embedding
                </b>
                Fetching and extracting is quick; the semantic index is what takes days.
                Pause embedding above to take the text now and index it later — the
                documents stay searchable by phrase either way.
              </div>
            )}

            {startForm10.data && (
              <div className="notice">
                <b>
                  {startForm10.data.queued > 0
                    ? `${startForm10.data.queued} quarters queued`
                    : "Nothing new to queue"}
                </b>
                {startForm10.data.alreadyQueued > 0
                  ? `${startForm10.data.alreadyQueued} were already queued. `
                  : ""}
                They run at backfill priority, below everything that moves a live filing
                toward a situation.
              </div>
            )}

            <p className="ops-actions">
              {/*
                Gated on what the range still needs, not on whether anything is in
                flight: `queued` counts every `backfill_window` job regardless of range,
                so gating on it would hide the button when widening a pass. Enqueueing is
                deduped per quarter, so a press while one drains queues only what has no
                job.
              */}
              {form10.data.quartersDone < form10.data.quarters && (
                <button
                  className="iconbtn"
                  onClick={() => startForm10.mutate()}
                  disabled={startForm10.isPending || form10.data.paused}
                >
                  {startForm10.isPending
                    ? "Queuing…"
                    : `Walk ${form10.data.quarters - form10.data.quartersDone} quarters`}
                </button>
              )}

              {(form10.data.queued > 0 || form10.data.running > 0) && (
                <span className="hint">
                  {form10.data.running > 0 ? `${form10.data.running} running · ` : ""}
                  {form10.data.queued} quarters queued
                  {form10.data.paused ? " · paused" : ""}
                </span>
              )}

              {(form10.data.queued > 0 || form10.data.running > 0 || form10.data.paused) && (
                <button
                  className="iconbtn"
                  aria-pressed={form10.data.paused}
                  disabled={pause.isPending}
                  onClick={() =>
                    pause.mutate({ kind: "backfill_window", paused: !form10.data!.paused })
                  }
                >
                  {form10.data.paused ? "Resume backfill" : "Pause backfill"}
                </button>
              )}

              {form10.data.quartersDone >= form10.data.quarters &&
                form10.data.queued === 0 &&
                form10.data.running === 0 && (
                  <span className="hint">Every quarter in range has been walked.</span>
                )}
            </p>
          </>
        )}

        {/*
          A change in policy does not reach backwards on its own, so reading what is
          already here is a decision made here, with its size in front of the person
          making it.
        */}
        <div className="sechd">Read the backlog</div>
        <p className="deck">
          Filings arriving from now on queue their own read. Situations already here do
          not — a change in policy does not reach backwards — so this is the control that
          reads them. A read can answer that a filing describes no separation at all, and
          dismiss the situation on its own reasoning.
        </p>
        {backlog.data && (
          <>
            <div className="terms">
              {/*
                Unread, not unreviewed. `disposition` is the owner's axis, so a read that
                confirms a separation leaves it `unreviewed`; counting that as backlog
                would offer the same situations to every press.
              */}
              <div>
                <div className="k">Unread</div>
                <div className={`v${backlog.data.estimate.pending > 0 ? " hi" : ""}`}>
                  {backlog.data.estimate.pending}
                </div>
                <div className="src">with a filing worth reading</div>
              </div>
              {/*
                Read, and waiting on a person. Not a backlog and not nothing — "0 to
                read" alone is what a finished pass and an empty corpus both look like.
              */}
              {backlog.data.estimate.alreadyRead > 0 && (
                <div>
                  <div className="k">Read, unjudged</div>
                  <div className="v">{backlog.data.estimate.alreadyRead}</div>
                  <div className="src">yours to disposition</div>
                </div>
              )}
              {backlog.data.estimate.inFlight > 0 && (
                <div>
                  <div className="k">In flight</div>
                  <div className="v">{backlog.data.estimate.inFlight}</div>
                  <div className="src">queued or running already</div>
                </div>
              )}
              {/*
                Unreviewed situations no read can reach. Stated rather than folded away:
                a pass that queues fewer than the backlog has to say where the rest went,
                or a partial pass reads as a finished one.
              */}
              {backlog.data.estimate.unreadable > 0 && (
                <div>
                  <div className="k">Unreadable</div>
                  <div className="v unknown">{backlog.data.estimate.unreadable}</div>
                  <div className="src">no extracted text yet</div>
                </div>
              )}
            </div>

            {startBacklog.data?.scope === "unread" && (
              <div className="notice">
                <b>
                  {startBacklog.data.queued > 0
                    ? `${startBacklog.data.queued} situations queued`
                    : "Nothing new to queue"}
                </b>{" "}
                They run at backfill priority, below a read you start from a situation
                page, and each waits for a Claude Code session to claim it.
              </div>
            )}

            <p className="ops-actions">
              {backlog.data.estimate.pending > 0 && (
                <button
                  className="iconbtn"
                  onClick={() => startBacklog.mutate("unread")}
                  disabled={startBacklog.isPending}
                >
                  {startBacklog.isPending && startBacklog.variables === "unread"
                    ? "Queuing…"
                    : `Read ${backlog.data.estimate.pending} situation${
                        backlog.data.estimate.pending === 1 ? "" : "s"
                      }`}
                </button>
              )}
              {/*
                With nothing to read the button is absent, and an absent button beside no
                sentence is indistinguishable from a panel that has failed to load. Which
                quiet state this is matters: everything read is a finished pass, whereas
                nothing readable at all is a corpus with no extracted text behind it.
              */}
              {backlog.data.estimate.pending === 0 && (
                <span className="hint">{backlogQuietSentence(backlog.data.estimate)}</span>
              )}
            </p>
          </>
        )}

        {/*
          Re-reading what has already been read, which the backlog above deliberately
          will not do.

          The two controls answer different questions and are kept apart. The backlog
          asks *what has never been read*, and stops offering a situation once its target
          document has been read; conflating the two would make it refill itself. This
          asks *what would a reader with today's questions say*, which for a situation
          read before a question existed is a different answer.

          It is a control rather than automatic: a change in policy does not reach
          backwards over the record on its own.
        */}
        <div className="sechd">Re-read everything</div>
        <p className="deck">
          The reader's questions change as it learns what to ask, and a situation read
          before a question existed does not answer it. A read is keyed on the exact
          prompt, so one whose question has not changed is answered from what is already
          stored and costs no model call; only the genuinely new ones are read again.
        </p>
        {backlog.data && (
          <>
            <div className="terms">
              <div>
                <div className="k">Every situation</div>
                {/*
                  Not accented. The backlog above takes the accent because it is work: it
                  falls as reads finish and reaches zero when the pass is done. This one is
                  a population, not a queue — every situation with a filing — so it cannot
                  reach zero while the corpus has anything in it. Accented, it would be an
                  attention signal no amount of work could clear.
                */}
                <div className="v">{backlog.data.estimate.rereadable}</div>
                {/*
                  Says outright that this overlaps the backlog above, because two counts in
                  one panel invite addition; and that it does not fall, because the number
                  staying put after a drain is correct rather than evidence the pass did
                  nothing.
                */}
                <div className="src">
                  {backlog.data.estimate.pending > 0
                    ? `read or unread, including the ${backlog.data.estimate.pending} above · does not fall as reads finish`
                    : "read or unread · does not fall as reads finish"}
                </div>
              </div>
              {/*
                What "everything" left out: a pass that queues fewer than it says has to
                name the difference.
              */}
              {backlog.data.estimate.dismissed > 0 && (
                <div>
                  <div className="k">Excluded</div>
                  <div className="v unknown">{backlog.data.estimate.dismissed}</div>
                  <div className="src">judged never a separation</div>
                </div>
              )}
            </div>

            {startBacklog.data?.scope === "all" && (
              <div className="notice">
                <b>
                  {startBacklog.data.queued > 0
                    ? `${startBacklog.data.queued} situation${
                        startBacklog.data.queued === 1 ? "" : "s"
                      } queued for a re-read`
                    : "Nothing to re-read"}
                </b>{" "}
                {startBacklog.data.queued > 0
                  ? "They run at backfill priority, below a read you start from a situation page, and each waits for a Claude Code session to claim it. A situation whose question has not changed is answered from the stored response without a model call."
                  : "Every situation with a filing to read already has a read queued or running."}
                {/*
                  Selected and then found to have no readable document after all. Counted
                  rather than dropped, or a partial pass reads as finished.
                */}
                {startBacklog.data.unreadable > 0 &&
                  ` ${startBacklog.data.unreadable} had no readable document after all.`}
              </div>
            )}

            <p className="ops-actions">
              {backlog.data.estimate.rereadable > 0 && (
                <button
                  className="iconbtn"
                  onClick={() => startBacklog.mutate("all")}
                  disabled={startBacklog.isPending}
                >
                  {startBacklog.isPending && startBacklog.variables === "all"
                    ? "Queuing…"
                    : `Re-read ${backlog.data.estimate.rereadable} situation${
                        backlog.data.estimate.rereadable === 1 ? "" : "s"
                      }`}
                </button>
              )}
              {backlog.data.estimate.rereadable === 0 && (
                <span className="hint">{rereadQuietSentence(backlog.data.estimate)}</span>
              )}
            </p>
          </>
        )}

        {/*
          The grids the reader renders instead of a line of run-together figures. A
          button rather than a migration: nothing reaches backwards over the corpus on
          its own.
        */}
        <div className="sechd">Table grids</div>
        <p className="deck">
          Rebuilds the tables in each document from markup already stored, so the reader
          can show a capitalization table as a table. It fetches nothing and spends
          nothing. Documents without grids read as plain text, which is correct and
          merely less legible.
        </p>
        {tables.data && (
          <>
            <div className="terms">
              <div>
                <div className="k">Unbuilt</div>
                <div className={`v${tables.data.pending > 0 ? " hi" : ""}`}>
                  {tables.data.pending}
                </div>
                <div className="src">documents with no current grids</div>
              </div>
              <div>
                <div className="k">Built</div>
                <div className="v">{tables.data.built}</div>
                <div className="src">of {tables.data.total} with stored text</div>
              </div>
              <div>
                <div className="k">Rules</div>
                <div className="v">{tables.data.tablesVersion}</div>
                {/* Bumping this is what makes the pass rerunnable: the dedupe key
                    carries it, so one press per version is a no-op and the next
                    version is a genuinely new question. */}
                <div className="src">bumped when the grid rules change</div>
              </div>
              {/*
                Documents this button cannot fix, named rather than folded into the
                pending count, which would then never reach zero. A grid's offsets index
                the stored text, so text from another extractor needs re-extracting
                before a grid built for it would describe it.
              */}
              {tables.data.staleText > 0 && (
                <div>
                  <div className="k">Older text</div>
                  <div className="v unknown">{tables.data.staleText}</div>
                  <div className="src">needs re-extracting first</div>
                </div>
              )}
              {tables.data.withoutBytes > 0 && (
                <div>
                  <div className="k">No bytes</div>
                  <div className="v unknown">{tables.data.withoutBytes}</div>
                  <div className="src">no stored markup to rebuild from</div>
                </div>
              )}
            </div>

            {startTables.data && (
              <div className="notice">
                <b>
                  {startTables.data.queued > 0
                    ? `${startTables.data.queued} documents queued`
                    : "Nothing new to queue"}
                </b>
                {startTables.data.alreadyQueued > 0
                  ? `${startTables.data.alreadyQueued} were already queued. `
                  : ""}
                Largest documents first — an information statement is where the tables
                worth reading as grids are — and at backfill priority, so nothing that
                moves a live filing toward a situation waits on this.
              </div>
            )}

            <p className="ops-actions">
              {tables.data.pending > 0 && (
                <button
                  className="iconbtn"
                  onClick={() => startTables.mutate()}
                  disabled={startTables.isPending || tables.data.paused}
                >
                  {startTables.isPending
                    ? "Queuing…"
                    : `Build ${tables.data.pending} document${tables.data.pending === 1 ? "" : "s"}`}
                </button>
              )}

              {(tables.data.queued > 0 || tables.data.running > 0) && (
                <span className="hint">
                  {tables.data.running > 0 ? `${tables.data.running} running · ` : ""}
                  {tables.data.queued} queued
                  {tables.data.paused ? " · paused" : ""}
                </span>
              )}

              {(tables.data.queued > 0 || tables.data.running > 0 || tables.data.paused) && (
                <button
                  className="iconbtn"
                  aria-pressed={tables.data.paused}
                  disabled={pause.isPending}
                  onClick={() => pause.mutate({ kind: "build_tables", paused: !tables.data!.paused })}
                >
                  {tables.data.paused ? "Resume building" : "Pause building"}
                </button>
              )}

              {/* Every quiet state says which one it is. An empty panel is what an
                  empty corpus and a finished pass look like alike. */}
              {tables.data.pending === 0 &&
                tables.data.queued === 0 &&
                tables.data.running === 0 && (
                  <span className="hint">
                    {tables.data.total === 0
                      ? "No document has been extracted yet."
                      : "Every readable document has current grids."}
                  </span>
                )}
            </p>
          </>
        )}

        {/*
          The count, and the way to the page that can act on it. The control itself lives
          at `/ops/review`: judging a fold means reading the filings beside it, and
          Operations is the screen for noticing that something has stopped — not for
          reading documents.
        */}
        <div className="sechd">Reconciliation review</div>
        <p className="deck">
          Companies carrying more than one live situation, and filings that could not be
          placed on one. Both are the same question — which separation does this belong to
          — and both are proposed rather than decided.
        </p>
        <p className="ops-actions">
          {duplicates.data && (
            <span className="hint">
              {duplicates.data.groups.length === 0
                ? "No company has more than one live situation"
                : `${duplicates.data.groups.length} compan${
                    duplicates.data.groups.length === 1 ? "y" : "ies"
                  } with duplicates`}
              {unplaced.data ? ` · ${unplaced.data.total} filing(s) unplaced` : ""}
            </span>
          )}
          <Link className="iconbtn" to="/ops/review">
            Review
          </Link>
        </p>

        <div className="sechd">Announced terms</div>
        <p className="deck">
          A record date and a distribution date are blank in every version of an
          information statement — the board fixes them once the registration is effective
          and the parent announces them in an 8-K. The historical walk fetches Form 10s
          only, so those announcements are in no corpus. This asks EDGAR for the filing
          history of the companies already on each situation and fetches the few 8-Ks
          around the registration.
        </p>
        {terms.data && (
          <>
            <div className="terms">
              <div>
                <div className="k">Missing a date</div>
                <div className={`v${terms.data.estimate.pending > 0 ? " hi" : ""}`}>
                  {terms.data.estimate.pending}
                </div>
                <div className="src">situations with a Form 10</div>
              </div>
              {/*
                A subset of the count beside it, never a deduction from it. On a `10-12B`
                the filer is the SpinCo, so the parent — the company whose 8-K carries the
                dates — is unknown until a read names it. These are still walked, because a
                SpinCo can restate the dates in its own current report.
              */}
              {terms.data.estimate.parentUnknown > 0 && (
                <div>
                  <div className="k">Parent unknown</div>
                  <div className="v unknown">{terms.data.estimate.parentUnknown}</div>
                  <div className="src">of those · only the SpinCo to ask</div>
                </div>
              )}
              {terms.data.estimate.inFlight > 0 && (
                <div>
                  <div className="k">In flight</div>
                  <div className="v">{terms.data.estimate.inFlight}</div>
                  <div className="src">queued or running</div>
                </div>
              )}
            </div>

            {startTerms.data && (
              <div className="notice">
                <b>
                  {startTerms.data.queued > 0
                    ? `${startTerms.data.queued} situation${startTerms.data.queued === 1 ? "" : "s"} queued`
                    : "Nothing new to queue"}
                </b>
                One request per side, at backfill priority. What it fetches goes through
                the ordinary pipeline — the Screen judges it, reconciliation attaches it,
                and `extract_terms` reads the front of it for free. No Claude.
              </div>
            )}

            <p className="ops-actions">
              {terms.data.estimate.pending > 0 && (
                <button
                  className="iconbtn"
                  onClick={() => startTerms.mutate()}
                  disabled={startTerms.isPending}
                >
                  {startTerms.isPending
                    ? "Queuing…"
                    : `Fetch announcements for ${terms.data.estimate.pending} situation${
                        terms.data.estimate.pending === 1 ? "" : "s"
                      }`}
                </button>
              )}
              {/*
                Two quiet states: nothing to do, and a pass still running. A blank panel
                is what both would look like.
              */}
              {terms.data.estimate.pending === 0 && (
                <span className="hint">
                  {terms.data.estimate.inFlight > 0
                    ? "Every situation missing a date is already queued."
                    : "No situation with a Form 10 is missing a date."}
                </span>
              )}
            </p>
          </>
        )}

        <div className="sechd">Filings the old gate turned away</div>
        <p className="deck">
          Opening a situation and attaching to one used to be the same gate, and it runs
          before reconciliation knows whether a situation for this filer exists. It is
          tuned to reject backward references — which is what a completion 8-K is, and
          what a board's notice that it has fixed the record date is. So the document
          carrying the terms was fetched, extracted and attached to nothing. Attaching is
          its own question now; this re-asks it about the filings that arrived before.
        </p>
        {attachments.data && (
          <>
            <div className="terms">
              <div>
                <div className="k">Unattached</div>
                <div className={`v${attachments.data.estimate.pending > 0 ? " hi" : ""}`}>
                  {attachments.data.estimate.pending}
                </div>
                <div className="src">8-Ks from a situation's own companies</div>
              </div>
              {attachments.data.estimate.inFlight > 0 && (
                <div>
                  <div className="k">In flight</div>
                  <div className="v">{attachments.data.estimate.inFlight}</div>
                  <div className="src">reconciliations queued or running</div>
                </div>
              )}
            </div>

            {startAttachments.data && (
              <div className="notice">
                <b>
                  {startAttachments.data.queued > 0
                    ? `${startAttachments.data.queued} filing${startAttachments.data.queued === 1 ? "" : "s"} queued`
                    : "Nothing new to queue"}
                </b>
                Re-reconciled at backfill priority. Nothing here makes a sound: a
                notification and a deep read are both gated on the filing being recent,
                and these are years old. What runs is the free terms pass.
              </div>
            )}

            <p className="ops-actions">
              {attachments.data.estimate.pending > 0 && (
                <button
                  className="iconbtn"
                  onClick={() => startAttachments.mutate()}
                  disabled={startAttachments.isPending}
                >
                  {startAttachments.isPending
                    ? "Queuing…"
                    : `Re-reconcile ${attachments.data.estimate.pending} filing${
                        attachments.data.estimate.pending === 1 ? "" : "s"
                      }`}
                </button>
              )}
              {/*
                Two quiet states, and a blank panel is what both look like. Nothing left
                to attach and a pass still draining want different responses.
              */}
              {attachments.data.estimate.pending === 0 && (
                <span className="hint">
                  {attachments.data.estimate.inFlight > 0
                    ? "Every unattached filing is already queued."
                    : "Every situation missing a term holds the filings that could answer it."}
                </span>
              )}
            </p>
          </>
        )}

        <div className="sechd">Terms re-read</div>
        <p className="deck">
          The free pass reads the front of every filing a situation is about, and it is
          enqueued under a key with no version in it — so a pair read once is read for
          ever, whatever the patterns say afterwards. This re-asks it wherever the answer
          came from older ones. It fetches nothing and spends nothing; a{" "}
          <code>rule</code> fact still cannot land on top of a read or an override.
        </p>
        {termsRerun.data && (
          <>
            <div className="terms">
              <div>
                <div className="k">To re-read</div>
                <div className={`v${termsRerun.data.pending > 0 ? " hi" : ""}`}>
                  {termsRerun.data.pending}
                </div>
                <div className="src">of {termsRerun.data.total} attachments with text</div>
              </div>
              <div>
                <div className="k">Patterns</div>
                <div className="v">{termsRerun.data.termsVersion}</div>
                <div className="src">
                  {termsRerun.data.lastReadAt
                    ? `last read ${new Date(termsRerun.data.lastReadAt).toLocaleString()}`
                    : "nothing read at this version yet"}
                </div>
              </div>
              {/*
                Both exclusions are stated rather than folded into the total. A pair with
                no stored text and a relevant-not-subject attachment are each declined
                before a document is opened, so neither can ever drain, and a pass that
                quietly dropped them would report "everything is current" over them.
              */}
              {termsRerun.data.withoutText > 0 && (
                <div>
                  <div className="k">No text</div>
                  <div className="v">{termsRerun.data.withoutText}</div>
                  <div className="src">nothing stored for the pass to read</div>
                </div>
              )}
              {termsRerun.data.relevantOnly > 0 && (
                <div>
                  <div className="k">Relevant only</div>
                  <div className="v">{termsRerun.data.relevantOnly}</div>
                  <div className="src">evidence, not the subject — never extracted</div>
                </div>
              )}
            </div>

            {startTermsRerun.data && (
              <div className="notice">
                <b>
                  {startTermsRerun.data.queued > 0
                    ? `${startTermsRerun.data.queued} pair${startTermsRerun.data.queued === 1 ? "" : "s"} queued`
                    : "Nothing new to queue"}
                </b>
                Queued at backfill priority. Every input is already in Postgres, so this
                is CPU that yields to the morning scan.
                {startTermsRerun.data.remaining > 0 &&
                  ` ${startTermsRerun.data.remaining} left for the next press.`}
              </div>
            )}

            <p className="ops-actions">
              {termsRerun.data.pending > 0 && (
                <button
                  className="iconbtn"
                  onClick={() => startTermsRerun.mutate()}
                  disabled={startTermsRerun.isPending}
                >
                  {startTermsRerun.isPending
                    ? "Queuing…"
                    : `Re-read ${termsRerun.data.pending} attachment${
                        termsRerun.data.pending === 1 ? "" : "s"
                      }`}
                </button>
              )}
              {/*
                A queued pair is still pending until the job runs, so "already queued" is
                never a state `pending === 0` can be in. It belongs beside the button, not
                instead of it.
              */}
              {termsRerun.data.pending > 0 && termsRerun.data.queued + termsRerun.data.running > 0 && (
                <span className="hint">
                  {termsRerun.data.queued + termsRerun.data.running} already queued — the
                  count falls as they run.
                </span>
              )}
              {/*
                Two quiet states, and a blank panel is what both look like. A corpus with
                nothing attached and one where every attachment is current want different
                responses.
              */}
              {termsRerun.data.pending === 0 && (
                <span className="hint">
                  {termsRerun.data.total === 0
                    ? "No situation holds a filing with stored text yet."
                    : `Every attachment was read under ${termsRerun.data.termsVersion}.`}
                </span>
              )}
              {termsRerun.data.paused && (
                <span className="hint">The free terms pass is paused — nothing will run.</span>
              )}
            </p>
          </>
        )}

        <div className="sechd">Screen re-triage</div>
        <p className="deck">
          Re-runs today's rules over text that is already stored. It fetches nothing from
          EDGAR and spends nothing on Claude, and it changes no situation — what it
          produces is the report below.
        </p>
        {retriage.data && (
          <>
            <div className="terms">
              <div>
                <div className="k">Old verdict</div>
                <div className={`v${retriage.data.pending > 0 ? " hi" : ""}`}>
                  {retriage.data.pending}
                </div>
                <div className="src">filings judged by earlier rules</div>
              </div>
              <div>
                <div className="k">Current</div>
                <div className="v">{retriage.data.screened}</div>
                <div className="src">of {retriage.data.total} with stored text</div>
              </div>
              <div>
                <div className="k">Ruleset</div>
                <div className="v">{retriage.data.screenVersion}</div>
                {/* Bumping this is what makes a pass rerunnable; the dedupe key
                    carries it, so one press per version is a no-op and the next
                    version is a genuinely new question. */}
                <div className="src">bumped when a pattern changes</div>
              </div>
              {/*
                Filings no pass can reach, said rather than quietly left out of the
                denominator, so "everything is current" is never reported over them.
              */}
              {retriage.data.withoutText > 0 && (
                <div>
                  <div className="k">Unreadable</div>
                  <div className="v unknown">{retriage.data.withoutText}</div>
                  <div className="src">no stored text to re-judge</div>
                </div>
              )}
            </div>

            {startRetriage.data && (
              <div className="notice">
                <b>
                  {startRetriage.data.queued > 0
                    ? `${startRetriage.data.queued} filings queued`
                    : "Nothing new to queue"}
                </b>
                {startRetriage.data.alreadyQueued > 0
                  ? `${startRetriage.data.alreadyQueued} were already queued. `
                  : ""}
                They run at backfill priority, below everything that moves a live filing
                toward a situation, so the morning scan is never delayed by this.
              </div>
            )}

            <p className="ops-actions">
              {retriage.data.pending > 0 && (
                <button
                  className="iconbtn"
                  onClick={() => startRetriage.mutate()}
                  disabled={startRetriage.isPending || retriage.data.paused}
                >
                  {startRetriage.isPending
                    ? "Queuing…"
                    : `Re-triage ${retriage.data.pending} filing${retriage.data.pending === 1 ? "" : "s"}`}
                </button>
              )}

              {(retriage.data.queued > 0 || retriage.data.running > 0) && (
                <span className="hint">
                  {retriage.data.running > 0 ? `${retriage.data.running} running · ` : ""}
                  {retriage.data.queued} queued
                  {retriage.data.paused ? " · paused" : ""}
                </span>
              )}

              {(retriage.data.queued > 0 ||
                retriage.data.running > 0 ||
                retriage.data.paused) && (
                <button
                  className="iconbtn"
                  aria-pressed={retriage.data.paused}
                  disabled={pause.isPending}
                  onClick={() =>
                    pause.mutate({ kind: "retriage_filing", paused: !retriage.data!.paused })
                  }
                >
                  {retriage.data.paused ? "Resume re-triage" : "Pause re-triage"}
                </button>
              )}

              {retriage.data.pending === 0 &&
                retriage.data.queued === 0 &&
                retriage.data.running === 0 && (
                  <span className="hint">Every readable filing carries the current verdict.</span>
                )}
            </p>
          </>
        )}

        {/* Two rows to read past cost far less than one wrong deletion costs to notice. */}
        <div className="sechd">Resting on no qualifying evidence</div>
        {unsupported.data && (
          <>
            <p className="deck">
              {unsupported.data.items.length === 0
                ? "Every live situation still rests on a filing today's gate would pass."
                : `${unsupported.data.items.length} of ${unsupported.data.situations} live situations rest on no filing today's gate would pass. Nothing has been changed — open one and decide.`}
            </p>

            {/*
              A half-finished pass otherwise reads as a finished one: a short list with
              many situations still unjudged says something different from one with none.
            */}
            {unsupported.data.pendingReview > 0 && (
              <p className="fin-caveat">
                {unsupported.data.pendingReview} situation
                {unsupported.data.pendingReview === 1 ? " is" : "s are"} not assessed yet —
                at least one attached filing still carries an older verdict. The list is
                incomplete until the pass above finishes.
              </p>
            )}

            {unsupported.data.items.length > 0 && (
              <div className="filings">
                {unsupported.data.items.map((s) => (
                  <div className="retriage-case" key={s.situationId}>
                    <Link className="filing" to={`/situations/${s.situationId}`}>
                      <span className="ft">{s.stage}</span>
                      <span className="fd">{s.announcedOn ?? "—"}</span>
                      <span className="fr">{s.spincoName}</span>
                      <span className="fn">
                        {s.filings.length} filing{s.filings.length === 1 ? "" : "s"}
                      </span>
                    </Link>
                    {/*
                      The evidence, quoted, not the verdict summarised: a name alone gives
                      the reader nothing to judge, and `triage_reasons` holds what the
                      document said.
                    */}
                    {s.filings.map((f) => (
                      <p className="retriage-why" key={f.accession}>
                        {f.formType} {f.filingDate} · {f.companyName ?? f.accession} · event{" "}
                        {f.eventHits} / weak {f.weakHits}
                        {f.reasons.length > 0 ? ` — ${f.reasons.join(" · ")}` : " — no match"}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="sechd">Cost</div>
        <div className="terms">
          <div>
            <div className="k">Extractions</div>
            <div className="v">{jobs.data?.spend.extractions ?? "—"}</div>
            {/* One row per PAID call, so this is a sum and not an estimate. A retry
                reuses the stored answer and does not add to it. */}
            <div className="src">paid Claude reads</div>
          </div>
          <div>
            <div className="k">Total spent</div>
            <div className="v hi">
              {jobs.data ? `$${scaleDecimal(jobs.data.spend.totalUsd, 0, 2)}` : "—"}
            </div>
            {jobs.data?.spend.since && (
              <div className="src">since {jobs.data.spend.since.slice(0, 10)}</div>
            )}
          </div>
          {/*
            Split from the total rather than folded into it, because the two behave
            differently: extraction is one priced press per situation, while Ask is many
            small charges whose sum is the thing that creeps. A single figure would hide
            which of them moved.
          */}
          <div>
            <div className="k">Questions</div>
            <div className="v">{jobs.data?.spend.questions ?? "—"}</div>
            <div className="src">
              {jobs.data ? `$${Number(jobs.data.spend.questionsUsd).toFixed(2)} on Ask` : "answered"}
            </div>
          </div>
          <div>
            <div className="k">Per extraction</div>
            {/*
              Extraction spend, not the combined total, which also carries Ask; divided by
              the paid extractions, not all of them, because a run on a subscription cost
              no dollars and would understate a figure that reads as a price.
            */}
            <div className="v">
              {jobs.data && jobs.data.spend.extractions - jobs.data.spend.onSubscription > 0
                ? `$${(
                    Number(jobs.data.spend.extractionUsd) /
                    (jobs.data.spend.extractions - jobs.data.spend.onSubscription)
                  ).toFixed(2)}`
                : "—"}
            </div>
            <div className="src">
              {jobs.data && jobs.data.spend.onSubscription > 0
                ? `average of ${jobs.data.spend.extractions - jobs.data.spend.onSubscription} paid · ` +
                  `${jobs.data.spend.onSubscription} on subscription`
                : "average"}
            </div>
          </div>
        </div>

        <div className="sechd">Dependencies</div>
        <div className="terms">
          <div>
            <div className="k">Database</div>
            <div className={`v${health.data?.database.ok ? "" : " hi"}`}>
              {health.data?.database.ok ? "ok" : "down"}
            </div>
            {/*
              Reachability and size are different facts and are reported as such: the
              size degrades to absent on its own without touching `ok`, because a
              catalog query that fails is not a database that is down.
            */}
            {size && <div className="src">{formatBytes(size.totalBytes)} on disk</div>}
          </div>
          <div>
            <div className="k">Ollama</div>
            <div className={`v${health.data?.ollama.ok ? "" : " unknown"}`}>
              {health.data?.ollama.ok ? "ok" : "unreachable"}
            </div>
            {/* Classification and embedding degrade without it; nothing else does. */}
            {!health.data?.ollama.ok && <div className="src">classification skipped</div>}
          </div>
          <div>
            <div className="k">EDGAR budget</div>
            <div className="v">{health.data?.edgarBudgetRemaining ?? "—"}/s</div>
          </div>
          <div>
            <div className="k">Version</div>
            <div className="v">{version.data?.version ?? "—"}</div>
            <div className="src">{version.data?.commit?.slice(0, 7)}</div>
          </div>
        </div>

        {/*
          What Mosaic has decided to interrupt the owner about, and whether it could. A
          channel that is configured but broken shows as a run of "not sent" in this
          history, where a quiet month shows nothing.
        */}
        <Panel
          title="Notifications"
          count={notify.data?.notifications.length ?? null}
        >
        <p className="ops-actions">
          {/*
            Which channels can send, resolved before it is stated: an unresolved query is
            not the same fact as an unconfigured channel.
          */}
          <span className="hint">
            {!notify.data
              ? "Checking channels…"
              : notify.data.configured.length > 0
                ? `${notify.data.configured.join(", ")} configured`
                : "No channel configured — set MATTERMOST_WEBHOOK_URL and restart the API."}
          </span>
        </p>

        {notify.data && notify.data.notifications.length === 0 && (
          <p className="deck">
            Nothing has been raised yet. Only a separation appearing, a stage moving, or a
            finding above <code>info</code> is worth a phone buzzing —
            and only for filings from the last ten days, so the historical walk stays
            silent.
          </p>
        )}
        {notify.data && notify.data.notifications.length > 0 && (
          <PanelTail
            items={notify.data.notifications}
            idOf={(n) => n.id}
            renderRow={(n, open, toggle) => (
              <NotificationRow notification={n} open={open} onToggle={toggle} />
            )}
            href="/ops/notifications"
            linkLabel="All notifications"
          />
        )}

        </Panel>

        <Panel
          title="Warnings"
          count={warnings.data?.total ?? null}
          countLabel="recorded"
        >
        {warnings.data && warnings.data.items.length === 0 && (
          <p className="deck">Nothing has degraded to null. That is the good case.</p>
        )}
        {warnings.data && warnings.data.items.length > 0 && (
          <PanelTail
            items={warnings.data.items}
            idOf={(w) => w.id}
            renderRow={(w, open, toggle) => (
              <WarningRow warning={w} open={open} onToggle={toggle} />
            )}
            href="/ops/warnings"
            linkLabel="All warnings"
          />
        )}

        </Panel>

        {/*
          Where the disk went. Text and vectors grow for different reasons — text with
          how many filings were fetched, vectors with how many were judged worth
          indexing — and only the second is a decision anyone made. A single total
          cannot show that; the split is the whole point of the section.
        */}
        <div className="sechd">Storage</div>
        {size && (
          <>
            <div className="terms">
              {size.groups
                // Financials is empty until a companyfacts fetch has run, and a 0 B row
                // is noise rather than information. The total still counts it.
                .filter((g) => g.bytes > 0)
                .map((g) => (
                  <div key={g.key}>
                    <div className="k">{g.label}</div>
                    <div className="v">{formatBytes(g.bytes)}</div>
                    <div className="src">{groupDetail(g.key, size.counts)}</div>
                  </div>
                ))}
              <div>
                <div className="k">Total</div>
                <div className="v">{formatBytes(size.totalBytes)}</div>
                {/* The parts above sum to exactly this — `Everything else` is the
                    remainder, not a list, so nothing can go missing between them. */}
                <div className="src">pg_database_size</div>
              </div>
            </div>

            {/*
              Raw filings are gzipped on disk under the filing store, not in Postgres, so a
              reader told only the database size would under-count the host.
            */}
            <p className="fin-caveat">
              Raw filings are gzipped on disk outside Postgres and are not counted here.
              Largest tables:{" "}
              {size.largest
                .slice(0, 4)
                .map((t) => `${t.table} ${formatBytes(t.bytes)}`)
                .join(" · ")}
              .
            </p>
          </>
        )}
        {/*
          Said rather than skipped. The section vanishing entirely would read as a
          database with nothing in it, which is the opposite of what a failed catalog
          query means.
        */}
        {health.data && !size && (
          <p className="deck">
            The size could not be read. The database is otherwise{" "}
            {health.data.database.ok ? "reachable" : "unreachable"}.
          </p>
        )}
      </article>
    </div>
  );
}

/**
 * What the fast poll is doing, and when the numbers on screen were true.
 *
 * All three states are named. A panel that says nothing while it has quietly stopped
 * refreshing is the same failure as a pipeline that has quietly stopped working, which
 * is the thing this screen exists to catch.
 */
function cadenceLabel(activity: OpsActivity | null, live: boolean, visible: boolean): string {
  const at = activity ? `as of ${new Date(activity.observedAt).toLocaleTimeString()}` : null;
  const cadence = !live
    ? "not polling"
    : visible
      ? `polling every ${ACTIVITY_POLL_MS / 1_000}s`
      : "not polling while this tab is hidden";
  return at ? `${cadence} · ${at}` : `${cadence} · nothing read yet`;
}

/** How long this job has been going, and the evidence that its worker is still there. */
function elapsedLabel(job: OpsActivity["running"][number]): string {
  /**
   * `started_at` and `locked_at` are different facts and both are shown. The lease is
   * renewed while the worker runs, so `locked_at` says a live worker held this recently
   * and cannot say how long it has been running.
   */
  // Bare, because the two places it is rendered supply their own frame: the summary row
  // reads "running 19m 25s" and the detail line is labelled "running for".
  return job.runningSeconds === null
    ? "not recorded — claimed before this was timed"
    : formatDuration(job.runningSeconds);
}

/**
 * What this claim's lease says, in words rather than as a number.
 *
 * A lapsed lease clears itself, so the row says what happens next as well as what is
 * true now: a worker stopped without releasing the job, and the reaper returns it.
 */
function leaseLabel(job: OpsActivity["running"][number]): string {
  if (job.leaseIsStale) {
    return (
      "lapsed — the worker holding this stopped without releasing it. The reaper will " +
      "return the job to the queue, refund the attempt, and it will run again."
    );
  }
  return `worker checked in ${formatDuration(job.leaseSeconds ?? 0)} ago — it renews every 30s while it works`;
}

/**
 * Nothing to read, said in the way that distinguishes the reasons.
 *
 * A finished pass, a batch still in flight, a corpus with nothing extracted, and a radar
 * with no situations on it all render as an absent button. The first is success and the
 * third is a pipeline that has not run, and they want opposite responses from the reader.
 */
function backlogQuietSentence(e: ExtractBacklogState["estimate"]): string {
  if (e.inFlight > 0) {
    return `${e.inFlight} already queued — each waits for a Claude Code session to claim it.`;
  }
  if (e.alreadyRead > 0) {
    return `Everything readable has been read. ${e.alreadyRead} ${
      e.alreadyRead === 1 ? "situation is" : "situations are"
    } waiting on your judgement, not on a read.`;
  }
  if (e.unreadable > 0) {
    return "Nothing here can be read — no filing of these situations has extracted text yet.";
  }
  return "No unreviewed situations.";
}

/**
 * Why there is nothing to re-read. Three different reasons, and a blank would be the
 * fourth thing they all look like.
 *
 * The zero here is a stronger statement than the backlog's: the population is every
 * situation with a filing, so an empty one usually means the whole set is already
 * queued rather than that the pass has finished.
 */
function rereadQuietSentence(e: ExtractBacklogState["estimate"]): string {
  if (e.inFlight > 0) {
    return `Every situation with a filing to read is already queued — ${e.inFlight} waiting for a Claude Code session to claim ${e.inFlight === 1 ? "it" : "them"}.`;
  }
  if (e.dismissed > 0) {
    return `Nothing to re-read. The ${e.dismissed} ${
      e.dismissed === 1 ? "situation" : "situations"
    } judged never to have been a separation ${e.dismissed === 1 ? "is" : "are"} excluded deliberately.`;
  }
  return "No situation here has a filing with extracted text to read.";
}

/**
 * Why the queue tail is empty, which is three different facts.
 *
 * Nothing pending at all is an idle machine; nothing pending while something is running
 * is a queue that has just drained to its last job; and the tail being empty while the
 * per-kind counts are not would be a bug worth seeing rather than a blank.
 */
function queuedQuietSentence(activity: OpsActivity): string {
  const pending = totalOf(activity, "pending");
  if (pending > 0) {
    // Only reachable if the tail and the counts disagree, which they are read from the
    // same snapshot to prevent. Said rather than rendered as a blank, because a panel
    // that goes quiet on an inconsistency is how one goes unnoticed.
    return `${pending} queued, but none could be listed — the tail and the counts disagree.`;
  }
  if (activity.running.length > 0) {
    return `Nothing is waiting. ${activity.running.length} job${
      activity.running.length === 1 ? " is" : "s are"
    } running, and it is the last of the queue.`;
  }
  return "Nothing is waiting to run.";
}

/** Which of the three quiet states this is. They want completely different responses. */
function idleSentence(activity: OpsActivity): string {
  const pending = totalOf(activity, "pending");
  if (pending === 0) return "Nothing is running, and nothing is queued.";

  /**
   * Runnable means not paused and past its `run_after`. A queue full of jobs waiting out
   * a retry backoff is idle for a reason; a queue full of runnable jobs with no worker
   * touching them is the failure this panel is for, and one pending count cannot tell
   * the two apart.
   */
  const runnable = activity.backlog
    .filter((k) => !k.paused && k.pending > 0 && (k.oldestPendingSeconds ?? -1) >= 0)
    .reduce((sum, k) => sum + k.pending, 0);
  if (runnable > 0) {
    return `Nothing is running, and ${runnable} job${runnable === 1 ? " is" : "s are"} runnable now — no worker has claimed one.`;
  }
  return `Nothing is running. ${pending} queued, none runnable yet: paused, or waiting out a retry backoff.`;
}

/**
 * The sum of one count across every job kind's backlog.
 *
 * @internal
 * @param activity The activity report, or undefined before it loads.
 * @param key Which count to sum.
 * @returns The total; 0 when there is no report.
 */
function totalOf(activity: OpsActivity | undefined, key: "pending" | "failed"): number {
  return (activity?.backlog ?? []).reduce((sum, kind) => sum + kind[key], 0);
}

/**
 * The oldest pending job's age. Negative is not an error: `run_after` is in the future
 * for a job waiting out its backoff, and saying "next attempt in 4m" is the difference
 * between a queue that is stuck and one that is deliberately waiting.
 */
function waitLabel(seconds: number | null): string {
  if (seconds === null) return "none pending";
  if (seconds < 0) return `next attempt in ${formatDuration(-seconds)}`;
  return `oldest queued ${formatDuration(seconds)} ago`;
}

/**
 * The live embedding rate, and what it implies for the work still queued.
 *
 * Every number names where it came from: the count and the window it was counted over,
 * this host's rate against the one the estimate above used, and the projection labelled
 * with the chunk count it divides. A rate with no denominator on screen is a number to
 * be trusted rather than checked.
 */
function embeddingLine(
  activity: OpsActivity,
  estimate: { estimatedChunks: number; pending: number; chunksPerSecond: number; chunksPerSecondSource: string } | undefined,
): string {
  const { chunks, chunksPerSecond, windowMinutes, lastChunkSecondsAgo } = activity.embedding;

  if (chunksPerSecond === null) {
    const last =
      lastChunkSecondsAgo === null
        ? "Nothing has ever been embedded."
        : `Last chunk written ${formatDuration(lastChunkSecondsAgo)} ago.`;
    return `No chunk in the last ${windowMinutes} minutes. ${last}`;
  }

  const against = estimate
    ? ` against ${estimate.chunksPerSecond}/s ${estimate.chunksPerSecondSource} in the estimate above`
    : "";
  const projection =
    estimate && estimate.pending > 0
      ? ` At that rate the ~${estimate.estimatedChunks.toLocaleString()} chunks still to write are ${formatDuration(
          estimate.estimatedChunks / chunksPerSecond,
        )}.`
      : "";
  return (
    `Embedding now: ${chunks.toLocaleString()} chunks in the last ${windowMinutes} minutes` +
    ` — ${chunksPerSecond}/s here${against}.${projection}`
  );
}

/**
 * What each storage group is made of, in the units the group is actually about.
 *
 * Bytes alone cannot say whether embeddings are large because the corpus is broad or
 * because one filing was chunked badly, and the counts that answer that are different
 * per group — documents are counted in characters, embeddings in chunks.
 */
function groupDetail(key: DatabaseSize["groups"][number]["key"], counts: DatabaseSize["counts"]): string {
  switch (key) {
    case "documents":
      return `${counts.extracted.toLocaleString()} extracted · ${(counts.characters / 1e6).toFixed(1)}M characters`;
    case "embeddings":
      return `${counts.chunks.toLocaleString()} chunks · ${counts.embeddedDocuments.toLocaleString()} documents`;
    case "financials":
      return "XBRL facts, every vintage kept";
    default:
      return "queue, situations, warnings, catalogs";
  }
}
